import type { CategorySummary, ExportBalance, HitlDecision } from './types'

export function formatCurrency(value: number, compact = false): string {
  if (compact) {
    const abs = Math.abs(value)
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
    if (abs >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export function formatPercent(value: number): string {
  const sign = value > 0 ? '+' : ''
  return `${sign}${(value * 100).toFixed(1)}%`
}

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${bytes} B`
}

export function categoryTone(category: CategorySummary): string {
  if (category.status === 'auto_approved') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
  }
  if (Math.abs(category.variance_pct) > 0.3) {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  }
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
}

export function balanceLabel(balance: ExportBalance): string {
  return balance.is_balanced
    ? `${formatCurrency(balance.debit_total)} balanced`
    : `${formatCurrency(balance.difference)} out of balance`
}

export function completionPercent(
  categories: CategorySummary[],
  decisions: Record<string, HitlDecision | undefined>,
): number {
  if (!categories.length) return 0
  const completed = categories.filter(category => decisions[category.id]).length
  return Math.round((completed / categories.length) * 100)
}

export function csvFromRows(rows: Record<string, string>[]): string {
  if (!rows.length) return ''
  const headers = Object.keys(rows[0])
  const escape = (value: string) => {
    if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`
    return value
  }
  return [
    headers.join(','),
    ...rows.map(row => headers.map(header => escape(row[header] ?? '')).join(',')),
  ].join('\n')
}
